import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiConsulta, apiPcf, type ConsultaFiltros, type ConsultaLinha, type DetalhePcf } from "../lib/api";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { confirmar } from "../lib/confirmar";
import { LegendaLinhas } from "../components/LegendaLinhas";
import { Calculadora } from "../components/Calculadora";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";

// consulta_producao TELA-1 ("Posição da Fábrica") + TELA-2 (detalhe da PCF).
// parametro: "" (livre, com filtros), "CONSULTA", "PEDIDO", "NAO_INICIADAS", "PARADAS", "ATRASADAS"
// ou o nome de uma etapa (tiles do painel).

interface Props {
  usuario: Usuario;
  parametro: string;
  numpcf?: number;
  numped?: number;
  nomcli?: string;
  onVoltar: () => void;
  onEditarPcf: (numpcf: number) => void; // TELA-2 botao-editar -> TELA-PCF
  onImprimirPcf: (numpcf: number) => void; // TELA-2 botao-imprimir -> r1cadpcf
}

const nf2 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// padrão BR (Ricardo 04/11): peso também com 2 casas na exibição
const nf4 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2 = (n: number) => nf2.format(n || 0);
const fmt4 = (n: number) => nf4.format(n || 0);

// TELA-1 Opened (L1535-1567): título por modo.
function tituloModo(p: string, numpcf?: number, numped?: number): string {
  if (!p) return "Posição da Fábrica";
  if (p === "CONSULTA") return `Consulta de PCF nº ${numpcf ?? ""}`;
  if (p === "PEDIDO") return `Posição da Fábrica - SOMENTE PCFs DO PEDIDO ${numped ?? ""}`;
  if (p === "NAO_INICIADAS") return "Posição da Fábrica - SOMENTE PCFs NÃO INICIADAS";
  if (p === "ATRASADAS") return "Posição da Fábrica - SOMENTE PCFs ATRASADAS";
  if (p === "PARADAS") return "Posição da Fábrica - SOMENTE PCFs PARADAS ENTRE AS ETAPAS";
  return `Posição da Fábrica - SOMENTE PCFs EM ${p}`;
}

// COR-TABELA (L656-672): entrega e tempo pintados conforme o atraso.
function corAtraso(st: ConsultaLinha["status"]): React.CSSProperties | undefined {
  if (st === "ATRASO_ETAPA") return { color: "#d32f2f", fontWeight: 600 };
  if (st === "ATRASO_DATA") return { background: "#d32f2f", color: "#fff" };
  if (st === "ATRASO_DATA_ETAPA") return { background: "#111", color: "#fff" };
  return undefined;
}

const ROTULO_ATRASO: Record<string, string> = {
  ATRASO_DATA: "ATRASADO DATA DE ENTREGA",
  ATRASO_ETAPA: "ATRASADO ETAPA",
  ATRASO_DATA_ETAPA: "ATRASADO DATA DE ENTREGA E ETAPA",
};

// botao-excel (L540-601): PRODUCAO.XLSX com 10 colunas (Pedido, Cliente, PCF, Situação, Produto,
// Metros, Entrega, Situação PCF, Atraso, Próxima etapa). Gerado como planilha Excel (XML 2003).
function exportarExcel(linhas: ConsultaLinha[]) {
  const esc = (v: string) => v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const cel = (v: string | number, num = false) =>
    num ? `<Cell><Data ss:Type="Number">${v}</Data></Cell>` : `<Cell><Data ss:Type="String">${esc(String(v))}</Data></Cell>`;
  const cab = ["Pedido", "Cliente", "PCF", "Situação", "Produto", "Metros", "Entrega", "Situação PCF", "Atraso", "Próxima Etapa"];
  const rows = [
    `<Row>${cab.map((c) => cel(c)).join("")}</Row>`,
    ...linhas.map(
      (l) =>
        `<Row>${cel(l.numped, true)}${cel(l.nomcli)}${cel(l.numpcf, true)}${cel(l.situacTela)}${cel(l.nompro)}${cel(l.qtdmet, true)}${cel(
          l.datent
        )}${cel(l.situacPcf)}${cel(ROTULO_ATRASO[l.status] ?? "")}${cel(l.proximaEtapa)}</Row>`
    ),
  ];
  const xml = `<?xml version="1.0" encoding="UTF-8"?><?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="Producao"><Table>${rows.join("")}</Table></Worksheet></Workbook>`;
  const url = URL.createObjectURL(new Blob([xml], { type: "application/vnd.ms-excel" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "PRODUCAO.xls";
  a.click();
  URL.revokeObjectURL(url);
}

export function ConsultaProducao({ usuario, parametro, numpcf, numped, nomcli, onVoltar, onEditarPcf, onImprimirPcf }: Props) {
  const { theme, toggle } = useTheme();
  const livre = !parametro;
  const [filtro, setFiltro] = useState<"Data de Entrega" | "Faixa de PCFs">("Data de Entrega");
  const [datIni, setDatIni] = useState("");
  const [datFim, setDatFim] = useState("");
  const [pcfIni, setPcfIni] = useState("");
  const [pcfFim, setPcfFim] = useState("");
  const [pedido, setPedido] = useState("");
  const [clientes, setClientes] = useState<string[]>([]);
  const [cliente, setCliente] = useState("TODOS");
  const [fin, setFin] = useState(false);
  const [can, setCan] = useState(false);
  const [atr, setAtr] = useState(false);
  const [etaabe, setEtaabe] = useState(false);
  const [etarec, setEtarec] = useState(false);
  const [ocultar, setOcultar] = useState(false);
  const [linhas, setLinhas] = useState<ConsultaLinha[] | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [detalhe, setDetalhe] = useState<DetalhePcf | null>(null);
  const [linhaDet, setLinhaDet] = useState<number | null>(null);
  const [calc, setCalc] = useState(false);
  const ord = useOrdenacao(linhas, {
    pcf: (l) => l.numpcf, cli: (l) => l.nomcli, ped: (l) => l.numped || null, tipo: (l) => l.tipo,
    ent: (l) => l.datent, env: (l) => l.datenv, sit: (l) => l.situacTela, ini: (l) => l.inicio, fim: (l) => l.fim,
    tempo: (l) => l.tempo, tmax: (l) => l.tempoMax, pos: (l) => l.posicao, qpos: (l) => l.qtdPosicoes || null,
    sitpcf: (l) => l.situacPcf, mat: (l) => l.nompro, pec: (l) => l.qtdpec, peso: (l) => l.pesoTeorico,
    met: (l) => l.qtdmet, frete: (l) => l.frete, prox: (l) => l.proximaEtapa,
  });
  const ordDet = useOrdenacao(detalhe?.linhas, {
    eta: (l) => l.processo, sit: (l) => l.situac, op: (l) => l.operador, data: (l) => (l.data ? `${l.data} ${l.hora ?? ""}`.trim() : null), hora: (l) => l.hora, tempo: (l) => l.tempo,
  });

  const montar = useCallback(async () => {
    const usaFaixa = livre && filtro === "Faixa de PCFs" && (Number(pcfIni) || Number(pcfFim));
    const usaData = livre && filtro === "Data de Entrega" && (datIni || datFim);
    const f: ConsultaFiltros = {
      parametro,
      filtro: usaFaixa ? "Faixa de PCFs" : usaData ? "Data de Entrega" : "",
      numpcfIni: parametro === "CONSULTA" ? numpcf ?? 0 : Number(pcfIni) || 0,
      numpcfFim: Number(pcfFim) || 0,
      datentIni: datIni,
      datentFim: datFim,
      numped: parametro === "PEDIDO" ? numped ?? 0 : Number(pedido) || 0,
      nomcli: livre ? cliente : nomcli ?? "",
      finalizadas: livre && fin,
      canceladas: livre && can,
      atrasadas: livre && atr,
      etaabePcfFin: livre && etaabe,
      etarec: livre && etarec,
      ocultarAbertas: livre && etaabe && ocultar,
    };
    try {
      setErro(null);
      setCarregando(true);
      setSel(null);
      const r = await apiConsulta.consultar(f);
      if (r.excedeu) setErro("Quantidade de Itens é superior ao máximo permitido.");
      setLinhas(r.linhas);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [livre, filtro, pcfIni, pcfFim, datIni, datFim, parametro, numpcf, numped, pedido, cliente, nomcli, fin, can, atr, etaabe, etarec, ocultar]);

  // Cabeçalho fixo ao rolar na vertical e colunas Cliente/Pedido fixas ao rolar na horizontal (Ricardo, 19/10/2026):
  // a grade rola dentro da própria caixa, que ocupa a altura que sobra da janela.
  const wrapRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ajustar = () => {
      el.style.maxHeight = `${Math.max(240, window.innerHeight - el.getBoundingClientRect().top - 24)}px`;
      // congelar painéis (como no Excel): PCF, Cliente e Pedido ficam fixos ao rolar para o lado
      const ths = el.querySelectorAll<HTMLElement>("thead th");
      el.style.setProperty("--col1", `${ths[0]?.offsetWidth ?? 0}px`);
      el.style.setProperty("--col2", `${(ths[0]?.offsetWidth ?? 0) + (ths[1]?.offsetWidth ?? 0)}px`);
    };
    ajustar();
    window.addEventListener("resize", ajustar);
    return () => window.removeEventListener("resize", ajustar);
  }, [linhas]);

  useEffect(() => {
    if (livre) apiConsulta.clientes().then(setClientes).catch(() => setClientes([]));
    else void montar(); // modos do painel/pedido: pesquisa ao abrir (L1596)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function abrirDetalhe(n: number) {
    try {
      setLinhaDet(null);
      setDetalhe(await apiConsulta.detalhe(n));
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  // Popup!cancelar_pcf (L732-753).
  async function cancelarPcf(n: number) {
    if (!(await confirmar("Confirma o cancelamento desta PCF?"))) return;
    const r = await apiPcf.cancelar(n);
    if (!r.ok) return setErro(r.mensagem);
    await montar();
  }

  async function excluirApontamento() {
    if (!detalhe || linhaDet == null) return;
    const l = detalhe.linhas[linhaDet];
    if (!l || l.posicao == null || l.sequen == null) return;
    if (!(await confirmar(`Excluir o apontamento "${l.processo} — ${l.situac}"?`))) return;
    const r = await apiConsulta.excluirApontamento({
      numpcf: detalhe.numpcf,
      posicao: l.posicao,
      sequen: l.sequen,
      etapa: l.etapa,
      situac: l.situac,
    });
    if (!r.ok) return setErro(r.mensagem);
    await abrirDetalhe(detalhe.numpcf);
  }

  const chk = (rot: string, v: boolean, set: (b: boolean) => void, dis = false) => (
    <label className="pcf-check" style={{ whiteSpace: "nowrap" }}>
      <input type="checkbox" checked={v} disabled={dis} onChange={(e) => set(e.target.checked)} />
      {rot}
    </label>
  );

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            Consulta de Produção
            <small>{tituloModo(parametro, numpcf, numped)}</small>
          </h1>
        </div>
        <span className="spacer" />
        <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
          <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
          <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
        </div>
        <button className="btn-secundario btn-voltar-painel" onClick={onVoltar}>
          ← Voltar
        </button>
        <button className="btn-secundario" onClick={toggle} title={theme === "light" ? "Modo escuro" : "Modo claro"}>{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
      </header>

      {livre && (
        <div className="plan-filtros">
          <label className="plan-campo">
            <span>Tipo de Filtro</span>
            <select value={filtro} onChange={(e) => setFiltro(e.target.value as typeof filtro)}>
              <option>Data de Entrega</option>
              <option>Faixa de PCFs</option>
            </select>
          </label>
          {filtro === "Data de Entrega" ? (
            <label className="plan-campo plan-campo-datas">
              <span>Data de Entrega</span>
              <div className="plan-datas">
                <input type="date" value={datIni} onChange={(e) => setDatIni(e.target.value)} />
                <span>a</span>
                <input type="date" value={datFim} onChange={(e) => setDatFim(e.target.value)} />
              </div>
            </label>
          ) : (
            <label className="plan-campo plan-campo-datas">
              <span>Faixa de PCF</span>
              <div className="plan-datas">
                <input className="num" inputMode="numeric" value={pcfIni} onChange={(e) => setPcfIni(e.target.value.replace(/\D/g, ""))} />
                <span>a</span>
                <input className="num" inputMode="numeric" value={pcfFim} onChange={(e) => setPcfFim(e.target.value.replace(/\D/g, ""))} />
              </div>
            </label>
          )}
          <label className="plan-campo">
            <span>Nº Pedido</span>
            <input className="num" inputMode="numeric" value={pedido} onChange={(e) => setPedido(e.target.value.replace(/\D/g, ""))} />
          </label>
          <label className="plan-campo">
            <span>Cliente</span>
            <select value={cliente} onChange={(e) => setCliente(e.target.value)}>
              <option>TODOS</option>
              {clientes.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <div className="pcf-checks" style={{ flexWrap: "wrap", rowGap: 8, flexBasis: "100%" }}>
            {chk("Ver Finalizadas", fin, setFin, atr)}
            {chk("Só Atrasadas", atr, (b) => {
              setAtr(b);
              if (b) {
                setFin(false);
                setCan(false);
              }
            })}
            {chk("Ver Canceladas", can, setCan, atr)}
            {chk("Mostrar etapas abertas de PCF's finalizadas", etaabe, (b) => {
              setEtaabe(b);
              if (!b) setOcultar(false);
            })}
            {chk("Ocultar Abertas", ocultar, setOcultar, !etaabe)}
            {chk("Mostrar apenas etapa mais recente", etarec, setEtarec)}
          </div>
          <button className="btn-acao" onClick={() => void montar()}>
            Pesquisar
          </button>
          <button className="btn-secundario" disabled={!linhas?.length} onClick={() => linhas && exportarExcel(ord.linhas)}>
            Exportar Planilha
          </button>
          <button className="btn-secundario" title="Calculadora" aria-label="Calculadora" onClick={() => setCalc(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="4" y="2" width="16" height="20" rx="2" />
              <rect x="7" y="5" width="10" height="4" />
              <path d="M8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01" />
            </svg>
          </button>
        </div>
      )}
      <LegendaLinhas />
      {calc && <Calculadora onFechar={() => setCalc(false)} />}

      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />

      <div className="tabela-wrap tabela-wrap-fixa" ref={wrapRef}>
        <table className="tabela-pcfs tabela-consulta">
          <thead>
            <tr>
              <ThOrdena ord={ord} col="pcf" className="num">PCF</ThOrdena>
              <ThOrdena ord={ord} col="cli">Cliente</ThOrdena>
              <ThOrdena ord={ord} col="ped" className="num">Pedido</ThOrdena>
              <ThOrdena ord={ord} col="tipo">Tipo</ThOrdena>
              <ThOrdena ord={ord} col="ent">Data Entrega</ThOrdena>
              <ThOrdena ord={ord} col="env">Envio Produc</ThOrdena>
              <ThOrdena ord={ord} col="sit">Etapa Atual / Situação</ThOrdena>
              <ThOrdena ord={ord} col="ini">Início da Produção</ThOrdena>
              <ThOrdena ord={ord} col="fim">Final da Produção</ThOrdena>
              <ThOrdena ord={ord} col="tempo" className="num">Tempo Prod.</ThOrdena>
              <ThOrdena ord={ord} col="tmax" className="num">Tempo Máx</ThOrdena>
              <ThOrdena ord={ord} col="pos" className="num">Etapa Atual</ThOrdena>
              <ThOrdena ord={ord} col="qpos" className="num">Qtd Etapas</ThOrdena>
              <ThOrdena ord={ord} col="sitpcf">Situação PCF</ThOrdena>
              <ThOrdena ord={ord} col="mat">Material</ThOrdena>
              <ThOrdena ord={ord} col="pec" className="num">Peças</ThOrdena>
              <ThOrdena ord={ord} col="peso" className="num">Peso</ThOrdena>
              <ThOrdena ord={ord} col="met" className="num">Qtd Metros</ThOrdena>
              <ThOrdena ord={ord} col="frete">Frete</ThOrdena>
              <ThOrdena ord={ord} col="prox">Prox Etapa</ThOrdena>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr>
                <td colSpan={21} className="plan-vazio">
                  Carregando…
                </td>
              </tr>
            )}
            {!carregando && linhas && linhas.length === 0 && (
              <tr>
                <td colSpan={21} className="plan-vazio">
                  Nenhuma PCF no filtro.
                </td>
              </tr>
            )}
            {!carregando &&
              ord.linhas.map((l, i) => (
                <tr
                  key={`${l.numpcf}-${l.posicao}-${i}`}
                  className={"row-clickable" + (sel === i ? " plan-sel" : "")}
                  onClick={() => setSel(i)}
                  onDoubleClick={() => void abrirDetalhe(l.numpcf)}
                  title="Duplo clique: detalhe da PCF"
                >
                  <td className="num">{l.numpcf}</td>
                  <td>{l.nomcli}</td>
                  <td className="num">{l.numped || ""}</td>
                  <td>{l.tipo}</td>
                  <td style={corAtraso(l.status)}>{l.datent}</td>
                  <td>{l.datenv}</td>
                  <td>{l.situacTela}</td>
                  <td>{l.inicio}</td>
                  <td>{l.fim}</td>
                  <td className="num" style={corAtraso(l.status)}>
                    {l.tempo}
                  </td>
                  <td className="num">{l.tempoMax}</td>
                  <td className="num">{l.posicao ?? ""}</td>
                  <td className="num">{l.qtdPosicoes || ""}</td>
                  <td>{l.situacPcf}</td>
                  <td>{l.nompro}</td>
                  <td className="num">{fmt2(l.qtdpec)}</td>
                  <td className="num">{fmt4(l.pesoTeorico)}</td>
                  <td className="num">{fmt2(l.qtdmet)}</td>
                  <td>{l.frete}</td>
                  <td>{l.proximaEtapa}</td>
                  <td className="pcf-row-acoes" onClick={(e) => e.stopPropagation()}>
                    {l.situacPcf !== "CANCELADA" && (
                      <button className="btn-mini btn-mini-del" title="Cancelar PCF" onClick={() => void cancelarPcf(l.numpcf)}>Cancelar</button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {detalhe && (
        <div className="pcf-modal-bg" onClick={() => setDetalhe(null)}>
          <div className="pcf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcf-modal-topo">
              <b>Detalhamento da Produção</b>
              <button className="btn-secundario" onClick={() => setDetalhe(null)}>Fechar</button>
            </div>
            <div className="pcf-grid">
              <label className="pcf-campo pcf-sm">
                <span>Número da PCF</span>
                <input className="num" value={String(detalhe.numpcf)} readOnly />
              </label>
              <label className="pcf-campo pcf-lg">
                <span>Produto Final</span>
                <input value={detalhe.nompro} readOnly />
              </label>
              {/* TELA-2.prc L301-306: Etapa atual = posição (ZZ) + nome da etapa; Situação = tempo_pcf.situac */}
              <label className="pcf-campo">
                <span>Etapa atual</span>
                <input value={[detalhe.posicao || "", detalhe.etapa].filter(Boolean).join(" - ")} readOnly />
              </label>
              <label className="pcf-campo">
                <span>Situação</span>
                <input value={detalhe.situac} readOnly />
              </label>
              <label className="pcf-campo">
                <span>Nome do Operador</span>
                <input value={detalhe.operador} readOnly />
              </label>
            </div>
            <div className="tabela-wrap">
              <table className="tabela-pcfs">
                <thead>
                  <tr>
                    <ThOrdena ord={ordDet} col="eta">Etapa</ThOrdena>
                    <ThOrdena ord={ordDet} col="sit">Situação</ThOrdena>
                    <ThOrdena ord={ordDet} col="op">Operador</ThOrdena>
                    <ThOrdena ord={ordDet} col="data">Data</ThOrdena>
                    <ThOrdena ord={ordDet} col="hora">Hora</ThOrdena>
                    <ThOrdena ord={ordDet} col="tempo" className="num">Tempo</ThOrdena>
                  </tr>
                </thead>
                <tbody>
                  {ordDet.linhas.map((l) => {
                    const i = ordDet.idx(l); // índice original: excluir apontamento usa detalhe.linhas[i]
                    return (
                    <tr
                      key={i}
                      className={"row-clickable" + (linhaDet === i ? " plan-sel" : "")}
                      onClick={() => setLinhaDet(linhaDet === i ? null : i)}
                    >
                      <td>{l.processo}</td>
                      <td>{l.situac}</td>
                      <td>{l.operador}</td>
                      <td>{l.data}</td>
                      <td>{l.hora}</td>
                      <td className="num">{l.tempo}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pcf-acoes">
              {/* L248-251 / L352-357: excluir só com PCF ABERTA, linha escolhida e permissão ATCADPCF */}
              <button
                className="btn-secundario plan-del"
                disabled={
                  linhaDet == null ||
                  detalhe.situacPcf !== "ABERTA" ||
                  !detalhe.podeAlterar ||
                  detalhe.linhas[linhaDet]?.situac === "NÃO INICIADA"
                }
                onClick={() => void excluirApontamento()}
              >
                Excluir Apontamento
              </button>
              <button className="btn-secundario" disabled={!detalhe.podeAlterar} onClick={() => onEditarPcf(detalhe.numpcf)}>
                Editar PCF
              </button>
              <button className="btn-secundario" disabled={!detalhe.podeAlterar} onClick={() => onImprimirPcf(detalhe.numpcf)}>
                Imprimir PCF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
